import { TextField } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";

import { FULL_TIME_SCHEDULER_PERMISSIONS } from "../../../config/permissions";
import { hasPermissionString } from "../../../utils/helpers";
import { StoreType } from "../../../utils/types";


interface UpdateDemandInputCardProps {
	shiftId: string;
	value: number | null;
	workcenterId: number;
	jobId: number;
	onChange: (
		demandId: string,
		newValue: any,
		workcenterId: number,
		jobId: number,
		shiftId: string
	) => void;
	demandId: string;
	shiftStartDateTime: string;
	isValueChanged?: boolean;
}

const UpdateDemandInputCard: React.FC<UpdateDemandInputCardProps> = ({
	shiftId,
	value,
	onChange,
	workcenterId,
	jobId,
	demandId,
	shiftStartDateTime,
	isValueChanged = false,
}) => {
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value ? parseInt(e.target.value, 10) : 0;
		onChange(demandId, newValue, workcenterId, jobId, shiftId);
	};

 
	const permissions = useSelector(
		(state: StoreType) => state.user?.userData?.permissions
	);

	const isDisabled = !hasPermissionString(
		permissions,
		FULL_TIME_SCHEDULER_PERMISSIONS.EDIT_DEMAND
	);
	const isPastShift = new Date(shiftStartDateTime) < new Date();

	// Prevent value change on scroll
	const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
		(e.currentTarget.querySelector("input") as HTMLElement)?.blur();
	};

	// Prevent value change with arrow keys and block the letter 'e'
	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		// Block 'e', 'E', '+', and '-' from being entered
		if (["e", "E", "+", "-"].includes(e.key)) {
			e.preventDefault();
		}

		// Prevent value change with up and down arrows
		if (e.key === "ArrowUp" || e.key === "ArrowDown") {
			e.preventDefault();
		}
	};

	return (
		<TextField
			variant='outlined'
			type='number'
			value={value ? value : ""}
			placeholder='0'
			onChange={handleChange}
			disabled={isDisabled}
			onWheel={handleWheel}
			onKeyDown={handleKeyDown}
			sx={{
				width: "100%",
				height: "100%",
				padding: "4px 4px",
				fontFamily: "Roboto",
				"& .MuiInputBase-root": {
					height: "100%",
					borderRadius: "8px",
					backgroundColor: isPastShift ? "#F7F7F7" : "none",
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: isPastShift ? "#9e9e9e" : "",
					},
					"&::placeholder": {
					color: "rgba(0,0,0,0.87)",
					opacity: isPastShift ? 0.5 : 1,
					},
				},
				"& .MuiInputBase-input": {
					textAlign: "center",
					fontWeight: 400,
					fontSize: "1.2rem",
				},
				"& .MuiOutlinedInput-notchedOutline": {
					border: "1px solid #E8E8E8",
					...(isValueChanged && {
                        borderStyle: "dashed",
						borderColor: "rgba(0,0,0,0.87)",
                    }),
				},
				// Hide the number input arrows
				"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
					{
						"WebkitAppearance": "none",
						margin: 0,
					},
				"& input[type=number]": {
					"MozAppearance": "textfield",
				},
				// ✅ placeholder selector for Chrome/Safari
				"& input::placeholder": {
					color: "rgba(0,0,0,0.87)",
					opacity: isPastShift ? 0.5 : 1,
					fontWeight: 400,
				},
				// ✅ placeholder selector for Firefox
				"& input::-moz-placeholder": {
					color: "rgba(0,0,0,0.87)",
					opacity: isPastShift ? 0.5 : 1,
					fontWeight: 400,
				},
			}}
		/>
	);
};

export default UpdateDemandInputCard;
